import { BookOpen, Target, Award, Globe2, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

const OurCulture = () => {
  const culturePillars = [
    {
      icon: <BookOpen className="w-10 h-10 text-[#007BFF]" />,
      title: 'Research Discipline',
      description: 'We approach energy intelligence with academic rigor and professional discipline. Every analysis follows structured methodologies, undergoes peer review, and meets defined quality standards.',
      highlights: [
        'Structured research frameworks',
        'Peer review protocols',
        'Quality assurance standards',
        'Methodology refinement'
      ]
    },
    {
      icon: <Target className="w-10 h-10 text-[#007BFF]" />,
      title: 'Continuous Learning',
      description: 'Energy markets evolve continuously, and maintaining analytical edge requires constant learning. We invest in team development and encourage intellectual curiosity.',
      highlights: [
        'Professional development',
        'Knowledge sharing',
        'Industry engagement',
        'Technical skill development'
      ]
    },
    {
      icon: <Award className="w-10 h-10 text-[#007BFF]" />,
      title: 'Respect for Data Integrity',
      description: 'Data quality underpins analytical credibility. We maintain rigorous data management practices, validate information sources, and document our methodologies transparently.',
      highlights: [
        'Rigorous validation',
        'Source documentation',
        'Transparent assumptions',
        'Clear communication'
      ]
    },
    {
      icon: <Globe2 className="w-10 h-10 text-[#007BFF]" />,
      title: 'Global Perspective',
      description: 'Energy is inherently global, and effective intelligence requires understanding diverse market contexts, regulatory frameworks, and regional dynamics.',
      highlights: [
        'Multi-regional expertise',
        'Cultural awareness',
        'Cross-market analysis',
        'Global collaboration'
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F7A4F] via-[#2C6E73] to-[#284A86] text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 whitespace-nowrap">Our Culture</h1>
            <p className="text-base lg:text-lg text-white/90 leading-relaxed">
              Building exceptional energy intelligence through intellectual rigor, collaborative excellence, and commitment to impact.
            </p>
          </div>
        </div>
      </section>

      {/* Culture Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1F3C88] mb-3">What Defines Our Culture</h2>
            <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
              Enerlligence culture centers on analytical excellence, collaborative intelligence, and professional integrity. We attract individuals who value substance, precision, and strategic impact.
            </p>
          </div>

          {/* 4-Column Horizontal Grid */}
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-6">
            {culturePillars.map((pillar, index) => (
              <div key={index} className="bg-slate-50 p-8 rounded-lg border border-slate-200 hover:border-[#007BFF] transition-all flex flex-col h-full">
                <div className="flex items-start space-x-3 mb-6">
                  {pillar.icon}
                  <h3 className="text-xl font-bold text-[#1F3C88]">{pillar.title}</h3>
                </div>
                
                <p className="text-base text-slate-600 leading-relaxed mb-6 flex-grow">{pillar.description}</p>
                
                <div className="border-t border-slate-300 pt-4">
                  <ul className="space-y-2">
                    {pillar.highlights.map((highlight, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <div className="w-1.5 h-1.5 bg-[#007BFF] rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-600">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Working at Enerlligence */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1F3C88] mb-3">Working at Enerlligence</h2>
            <p className="text-lg text-slate-600">What to expect as part of our team</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg border border-slate-200">
              <h3 className="text-xl font-semibold text-[#1F3C88] mb-4">Intellectual Challenge</h3>
              <p className="text-base text-slate-600 leading-relaxed">
                Engage with complex analytical problems, develop strategic insights, and contribute to intelligence that shapes market decisions.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg border border-slate-200">
              <h3 className="text-xl font-semibold text-[#1F3C88] mb-4">Collaborative Environment</h3>
              <p className="text-base text-slate-600 leading-relaxed">
                Work alongside experienced professionals across commodities, economics, and strategy in a culture of mutual respect and shared purpose.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg border border-slate-200">
              <h3 className="text-xl font-semibold text-[#1F3C88] mb-4">Professional Growth</h3>
              <p className="text-base text-slate-600 leading-relaxed">
                Develop deep expertise, expand analytical capabilities, and build a foundation for long-term career development in energy markets.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment Statement */}
      <section className="py-20 bg-white border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users className="w-16 h-16 text-[#007BFF] mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-[#1F3C88] mb-6">Commitment to Impact</h2>
          <p className="text-xl text-slate-700 leading-relaxed mb-6">
            We are committed to creating an environment where analytical excellence thrives, professional standards are maintained, and intelligence work contributes meaningfully to better energy outcomes.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed">
            Our culture reflects our values: integrity, collaboration, rigor, and forward thinking.
          </p>
        </div>
      </section>
    </div>
  );
};

export default OurCulture;
